import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import AuthShell from '../../components/layout/AuthShell';
import Btn from '../../components/ui/Btn';
import { Input, Spinner } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../lib/toast';

const MIN_PW = 8;

/**
 * ResetPasswordScreen — the landing page for the link in the reset email.
 *
 * Supabase appends a recovery token to the URL hash. Because the client is
 * created with detectSessionInUrl:true (see lib/supabase.js), supabase-js
 * exchanges that token for a session on load and fires PASSWORD_RECOVERY. We
 * wait for that session before enabling the form; if it never arrives (link
 * expired, already used, or tampered) we show an "expired link" state. The
 * grace-period timeout mirrors AcceptInviteScreen's verifying pattern.
 *
 * This route is intentionally public (not wrapped in RequireAuth): the user
 * has no session until the token is exchanged, and we want the dedicated reset
 * UI rather than RequireAuth's redirect to /sign-in.
 */
export default function ResetPasswordScreen() {
  const navigate = useNavigate();
  const toast = useToast();

  const [phase, setPhase] = useState('verifying'); // 'verifying' | 'ready' | 'invalid'
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let settled = false;
    const markReady = () => { if (!settled) { settled = true; setPhase('ready'); } };

    // Token exchange is async; listen for the recovery/sign-in event…
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) markReady();
    });

    // …and also check directly, in case the hash was already processed before
    // this component mounted (e.g. React StrictMode remount).
    supabase.auth.getSession().then(({ data }) => { if (data.session) markReady(); });

    // No session after the grace period → the link is invalid or expired.
    const timer = setTimeout(() => { if (!settled) setPhase('invalid'); }, 4000);

    return () => { subscription?.unsubscribe(); clearTimeout(timer); };
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setError(null);

    if (pw.length < MIN_PW) {
      setError(`Password must be at least ${MIN_PW} characters.`);
      return;
    }
    if (pw !== pw2) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password: pw });
      if (updErr) throw updErr;
      // Drop the recovery session so the user signs in fresh with the new
      // password — this both confirms the password works and stops SignInScreen
      // from immediately bouncing the still-active session to /hub.
      await supabase.auth.signOut();
      toast.success('Password updated. Please sign in with your new password.');
      navigate('/sign-in', { replace: true });
    } catch (err) {
      console.error('Password update failed:', err);
      setError(err.message || 'Could not update your password. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      {phase === 'verifying' && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 12, color: 'var(--text-3)', fontSize: 13, padding: '32px 0',
        }}>
          <Spinner size={20} color="var(--accent)" />
          Verifying your reset link…
        </div>
      )}

      {phase === 'invalid' && (
        <div>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'var(--danger-bg)', color: 'var(--danger)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16,
          }}>
            <Icon.alert size={22} />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em', marginBottom: 6 }}>
            This link has expired
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-3)', marginBottom: 22, lineHeight: 1.5 }}>
            Password reset links are valid for 1 hour and can only be used once.
            Head back to sign in and request a fresh one.
          </p>
          <Link to="/sign-in" style={{ textDecoration: 'none' }}>
            <Btn variant="primary" size="lg" style={{ width: '100%' }} iconRight={Icon.arrowR}>
              Back to sign in
            </Btn>
          </Link>
        </div>
      )}

      {phase === 'ready' && (
        <>
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.025em', marginBottom: 6 }}>
              Set a new password
            </h1>
            <p style={{ fontSize: 14, color: 'var(--text-3)' }}>
              Choose a strong password you don't use anywhere else.
            </p>
          </div>

          {error && (
            <div style={{
              padding: '10px 12px', background: 'var(--danger-bg)',
              border: '1px solid rgba(239,68,68,0.22)', borderRadius: 10,
              color: 'var(--danger)', fontSize: 12.5, lineHeight: 1.45,
              display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14,
            }}>
              <Icon.alert size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} noValidate>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
              New password
            </label>
            <Input
              icon={Icon.lock}
              type="password"
              autoComplete="new-password"
              autoFocus
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 8 characters"
              style={{ marginBottom: 14 }}
              disabled={submitting}
            />

            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
              Confirm new password
            </label>
            <Input
              icon={Icon.lock}
              type="password"
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="Re-enter your new password"
              style={{ marginBottom: 18 }}
              disabled={submitting}
            />

            <Btn
              type="submit"
              variant="primary"
              size="lg"
              style={{ width: '100%' }}
              iconRight={submitting ? null : Icon.arrowR}
              onClick={submit}
              disabled={submitting}
            >
              {submitting ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Updating…</> : 'Update password'}
            </Btn>
          </form>
        </>
      )}
    </AuthShell>
  );
}
